import React, { useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';
import { useRefresh } from '../utils/RefreshContext';
import {
  getPendingImages,
  getApprovedImages,
  approveImage,
  rejectImage,
  bulkApproveImages,
  bulkRejectImages,
} from '../services/imageModerationService';

const ImageApproval = () => {
  const { refreshKey } = useRefresh();

  // State management
  const [tab, setTab] = useState('pending'); // 'pending' or 'approved'
  const [images, setImages] = useState({});
  const [allImages, setAllImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedImageForRejection, setSelectedImageForRejection] = useState(null);

  // Fetch images
  useEffect(() => {
    fetchImages();
  }, [tab, refreshKey]);

  const fetchImages = async () => {
    try {
      setIsLoading(true);
      const response = await (tab === 'pending'
        ? getPendingImages()
        : getApprovedImages());

      // Group images by user
      const groupedByUser = {};
      const allImagesArray = response.data || response;

      if (Array.isArray(allImagesArray)) {
        allImagesArray.forEach(image => {
          const userId = image.user_id || image.uploaded_by;
          const userName = image.user_name || image.user?.full_name || 'Unknown User';
          const userEmail = image.user_email || image.user?.email || '';

          if (!groupedByUser[userId]) {
            groupedByUser[userId] = {
              userId,
              userName,
              userEmail,
              images: []
            };
          }
          groupedByUser[userId].images.push(image);
        });
      }

      setImages(groupedByUser);
      setAllImages(allImagesArray);
      setSelectedImages(new Set());
      setSelectedUser(Object.keys(groupedByUser)[0] || null); // Select first user by default
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load images');
    } finally {
      setIsLoading(false);
    }
  };

  // Get current user's images
  const getUserImages = () => {
    if (!selectedUser || !images[selectedUser]) return [];
    return images[selectedUser].images;
  };

  const getUserCount = () => {
    return Object.keys(images).length;
  };

  const getPendingCount = () => {
    return allImages.length;
  };

  // Handle image selection
  const handleImageSelect = (imageId) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };

  // Handle select all for current user
  const handleSelectAllUser = (checked) => {
    const userImages = getUserImages();
    const newSelected = new Set(selectedImages);

    if (checked) {
      userImages.forEach(img => newSelected.add(img.id));
    } else {
      userImages.forEach(img => newSelected.delete(img.id));
    }
    setSelectedImages(newSelected);
  };

  // Handle select all across all users
  const handleSelectAll = (checked) => {
    const newSelected = new Set();
    if (checked) {
      allImages.forEach(img => newSelected.add(img.id));
    }
    setSelectedImages(newSelected);
  };

  const isAllUserImagesSelected = () => {
    const userImages = getUserImages();
    if (userImages.length === 0) return false;
    return userImages.every(img => selectedImages.has(img.id));
  };

  const isAllImagesSelected = () => {
    return allImages.length > 0 && allImages.every(img => selectedImages.has(img.id));
  };

  // Approve single image
  const handleApproveImage = async (imageId) => {
    try {
      setIsProcessing(true);
      await approveImage(imageId);
      toast.success('Image approved');
      fetchImages();
    } catch (error) {
      console.error('Approve error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Reject single image
  const handleRejectImage = async (imageId, reason = '') => {
    try {
      setIsProcessing(true);
      await rejectImage(imageId, reason);
      toast.success('Image rejected');
      setShowRejectModal(false);
      setRejectReason('');
      fetchImages();
    } catch (error) {
      console.error('Reject error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedImages.size === 0) {
      toast.warning('Please select images first');
      return;
    }

    try {
      setIsProcessing(true);
      await bulkApproveImages(Array.from(selectedImages));
      toast.success(`${selectedImages.size} images approved`);
      fetchImages();
    } catch (error) {
      console.error('Bulk approve error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Bulk reject
  const handleBulkReject = async () => {
    if (selectedImages.size === 0) {
      toast.warning('Please select images first');
      return;
    }

    try {
      setIsProcessing(true);
      await bulkRejectImages(Array.from(selectedImages), rejectReason);
      toast.success(`${selectedImages.size} images rejected`);
      setShowRejectModal(false);
      setRejectReason('');
      fetchImages();
    } catch (error) {
      console.error('Bulk reject error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const userImages = getUserImages();
  const users = Object.values(images);

  if (isLoading && allImages.length === 0) return <Spinner />;

  return (
    <>
      <Topbar
        title="Image Management"
        subtitle={`${tab === 'pending' ? 'Approve or reject' : 'View'} user images`}
      />

      <div className="content">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 20, marginBottom: 24, borderBottom: '1px solid #E8EDF3', paddingBottom: 16 }}>
          <button
            onClick={() => setTab('pending')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 14,
              fontWeight: tab === 'pending' ? 700 : 500,
              color: tab === 'pending' ? 'var(--blue)' : 'var(--grey)',
              cursor: 'pointer',
              paddingBottom: 8,
              borderBottom: tab === 'pending' ? '2px solid var(--blue)' : 'none',
            }}
          >
            Pending ({getPendingCount()})
          </button>
          <button
            onClick={() => setTab('approved')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 14,
              fontWeight: tab === 'approved' ? 700 : 500,
              color: tab === 'approved' ? 'var(--blue)' : 'var(--grey)',
              cursor: 'pointer',
              paddingBottom: 8,
              borderBottom: tab === 'approved' ? '2px solid var(--blue)' : 'none',
            }}
          >
            Approved ({allImages.filter(img => img.status === 'approved').length})
          </button>
        </div>

        {/* User Selection */}
        <div style={{ marginBottom: 24 }}>
          <label className="form-label" style={{ marginBottom: 8 }}>Select User</label>
          <select
            value={selectedUser || ''}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="form-select"
          >
            <option value={null}>All Users ({getUserCount()})</option>
            {users.map(user => (
              <option key={user.userId} value={user.userId}>
                {user.userName} ({user.userEmail}) - {user.images.length} images
              </option>
            ))}
          </select>
        </div>

        {/* Bulk Actions Bar */}
        {selectedImages.size > 0 && tab === 'pending' && (
          <div className="bulk-bar">
            <span className="bulk-info">
              {selectedImages.size} image{selectedImages.size !== 1 ? 's' : ''} selected
            </span>
            <button
              className="bulk-btn"
              style={{ background: 'var(--green)', color: 'white' }}
              onClick={handleBulkApprove}
              disabled={isProcessing}
            >
              ✓ Approve All
            </button>
            <button
              className="bulk-btn"
              style={{ background: 'var(--red)', color: 'white' }}
              onClick={() => setShowRejectModal(true)}
              disabled={isProcessing}
            >
              ✕ Reject All
            </button>
          </div>
        )}

        {/* Images Grid */}
        {selectedUser ? (
          // Single user view
          <div>
            {userImages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'var(--grey)'
              }}>
                <p>No {tab} images for this user</p>
              </div>
            ) : (
              <>
                {/* Select all checkbox for this user */}
                {tab === 'pending' && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={isAllUserImagesSelected()}
                        onChange={(e) => handleSelectAllUser(e.target.checked)}
                      />
                      <span>Select All ({userImages.length})</span>
                    </label>
                  </div>
                )}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 16,
                }}>
                  {userImages.map(image => (
                    <ImageCard
                      key={image.id}
                      image={image}
                      isSelected={selectedImages.has(image.id)}
                      onSelect={() => handleImageSelect(image.id)}
                      onApprove={() => handleApproveImage(image.id)}
                      onReject={() => {
                        setSelectedImageForRejection(image.id);
                        setShowRejectModal(true);
                      }}
                      isProcessing={isProcessing}
                      showActions={tab === 'pending'}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          // All users view
          <div>
            {/* Select all checkbox */}
            {tab === 'pending' && allImages.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={isAllImagesSelected()}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <span>Select All ({allImages.length})</span>
                </label>
              </div>
            )}

            {users.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'var(--grey)'
              }}>
                <p>No {tab} images found</p>
              </div>
            ) : (
              users.map(user => (
                <div key={user.userId} style={{ marginBottom: 40 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 16,
                    paddingBottom: 12,
                    borderBottom: '1px solid #E8EDF3'
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #66B2FF, #0A1F44)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 14
                    }}>
                      {user.userName.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>
                        {user.userName}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--grey)' }}>
                        {user.userEmail}
                      </div>
                    </div>
                    <div style={{
                      background: 'var(--light)',
                      padding: '6px 12px',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--navy)'
                    }}>
                      {user.images.length} image{user.images.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 16,
                  }}>
                    {user.images.map(image => (
                      <ImageCard
                        key={image.id}
                        image={image}
                        isSelected={selectedImages.has(image.id)}
                        onSelect={() => handleImageSelect(image.id)}
                        onApprove={() => handleApproveImage(image.id)}
                        onReject={() => {
                          setSelectedImageForRejection(image.id);
                          setShowRejectModal(true);
                        }}
                        isProcessing={isProcessing}
                        showActions={tab === 'pending'}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => !isProcessing && setShowRejectModal(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 500 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: 16, color: 'var(--navy)', fontWeight: 700 }}>
              Reject Image{selectedImages.size > 1 ? 's' : ''}?
            </h2>

            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="form-textarea"
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={() => !isProcessing && setShowRejectModal(false)}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (selectedImageForRejection) {
                    handleRejectImage(selectedImageForRejection, rejectReason);
                  } else {
                    handleBulkReject();
                  }
                }}
                disabled={isProcessing}
              >
                {isProcessing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Image Card Component
const ImageCard = ({
  image,
  isSelected,
  onSelect,
  onApprove,
  onReject,
  isProcessing,
  showActions
}) => {
  const imageUrl = image.url || image.image_url || 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22%3E%3C/svg%3E';

  return (
    <div style={{
      border: isSelected ? '2px solid var(--blue)' : '1px solid #E8EDF3',
      borderRadius: 12,
      overflow: 'hidden',
      background: 'white',
      transition: 'all 0.2s',
      position: 'relative'
    }}>
      {showActions && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            width: 18,
            height: 18,
            cursor: 'pointer',
            zIndex: 10,
          }}
        />
      )}

      <div style={{
        width: '100%',
        aspectRatio: '1',
        background: 'var(--light)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <img
          src={imageUrl}
          alt={image.name || 'Image'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f0f4f8%22 width=%22100%22 height=%22100%22/%3E%3Ctext x=%2250%22 y=%2250%22 fill=%22%238a93a2%22 text-anchor=%22middle%22 dy=%22.3em%22%3ENo Image%3C/text%3E%3C/svg%3E';
          }}
        />
      </div>

      {image.name && (
        <div style={{
          padding: '8px',
          fontSize: 12,
          color: 'var(--grey)',
          borderTop: '1px solid #F0F4F8',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center'
        }}>
          {image.name}
        </div>
      )}

      {image.metadata && (
        <div style={{
          padding: '4px 8px',
          fontSize: 11,
          color: 'var(--grey)',
          borderTop: '1px solid #F0F4F8',
          textAlign: 'center',
          background: 'var(--light)'
        }}>
          {image.metadata}
        </div>
      )}

      {showActions && (
        <div style={{
          padding: '8px',
          display: 'flex',
          gap: 6,
          borderTop: '1px solid #F0F4F8'
        }}>
          <button
            className="btn btn-primary"
            style={{ flex: 1, fontSize: 11, padding: '6px 8px' }}
            onClick={onApprove}
            disabled={isProcessing}
          >
            ✓
          </button>
          <button
            className="btn btn-danger"
            style={{ flex: 1, fontSize: 11, padding: '6px 8px' }}
            onClick={onReject}
            disabled={isProcessing}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageApproval;


